import AgencyHelper from 'features/Agency/agencyHelper';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTRadio, { KTRadioType } from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import KTDropdownFilter from 'general/components/OtherKeenComponents/KTDropdownFilter';
import CustomDateRangePicker, {
  dateFilter,
  getDateFilterOption,
  resolveDateFilterFromRange,
} from 'general/components/Others/CustomDateRangePicker';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import UIHelper from 'general/helpers/UIHelper';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

AgencySaleOrderListFilterView.propTypes = {
  onCancel: PropTypes.func,
  onApply: PropTypes.func,
  initialFilter: PropTypes.object,
  defaultOrderType: PropTypes.string,
  filterVariant: PropTypes.string,
};

const invoiceStatusOptions = [
  { name: 'Tất cả', value: '' },
  { name: 'Đã xuất', value: '1' },
  { name: 'Chưa xuất', value: '0' },
];

const buyerRoleOptions = [
  { name: 'Tất cả', value: '' },
  ...DealerHelper.filterAgencyRoleOptions(),
];

const buildFilterStateFromInitial = (initialFilter) => {
  const createdFrom = initialFilter?.createdFrom ?? '';
  const createdTo = initialFilter?.createdTo ?? '';
  const from = initialFilter?.from ?? '';
  const to = initialFilter?.to ?? '';

  return {
    filterQ: initialFilter?.q ?? '',
    filterPrimaryRole: initialFilter?.primaryRole ?? '',
    filterBuyerType: initialFilter?.buyerType ?? '',
    filterStatus: initialFilter?.status ?? '',
    filterCreatedBy: initialFilter?.createdBy ?? '',
    filterInvoiceStatus: initialFilter?.invoiceStatus ?? '',
    filterOrderType: initialFilter?.orderType ?? '',
    timeFilterType: initialFilter?.timeFilterType ?? 'CREATED_DATE',
    filterDateFilter: initialFilter?.dateFilter
      || (from && to ? resolveDateFilterFromRange(from, to) : null),
    filterFrom: from,
    filterTo: to,
    filterCreatedDateFilter: initialFilter?.createdDateFilter
      || (createdFrom && createdTo ? resolveDateFilterFromRange(createdFrom, createdTo) : null),
    filterCreatedFrom: createdFrom,
    filterCreatedTo: createdTo,
  };
};

function AgencySaleOrderListFilterView({ onCancel, onApply, closeDropdown, initialFilter, defaultOrderType = '' }) {
  const initialState = useMemo(() => buildFilterStateFromInitial(initialFilter), []);
  const [filterQ, setFilterQ] = useState(initialState.filterQ);
  const [filterPrimaryRole, setFilterPrimaryRole] = useState(initialState.filterPrimaryRole);
  const [filterBuyerType, setFilterBuyerType] = useState(initialState.filterBuyerType);
  const [filterStatus, setFilterStatus] = useState(initialState.filterStatus);
  const [filterCreatedBy, setFilterCreatedBy] = useState(initialState.filterCreatedBy);
  const [filterInvoiceStatus, setFilterInvoiceStatus] = useState(initialState.filterInvoiceStatus);
  const [filterOrderType, setFilterOrderType] = useState(initialState.filterOrderType);
  const [timeFilterType, setTimeFilterType] = useState(initialState.timeFilterType);
  const [filterDateFilter, setFilterDateFilter] = useState(initialState.filterDateFilter);
  const [filterFrom, setFilterFrom] = useState(initialState.filterFrom);
  const [filterTo, setFilterTo] = useState(initialState.filterTo);
  const [filterCreatedDateFilter, setFilterCreatedDateFilter] = useState(initialState.filterCreatedDateFilter);
  const [filterCreatedFrom, setFilterCreatedFrom] = useState(initialState.filterCreatedFrom);
  const [filterCreatedTo, setFilterCreatedTo] = useState(initialState.filterCreatedTo);

  const buildFilterPayload = (overrides = {}) => ({
    page: 0,
    limit: AppConfigs.defaultPageLimit,
    q: filterQ,
    primaryRole: filterPrimaryRole,
    buyerType: filterBuyerType,
    status: filterStatus,
    createdBy: filterCreatedBy,
    invoiceStatus: filterInvoiceStatus,
    orderType: defaultOrderType?.length ? defaultOrderType : filterOrderType,
    contractStatus: isOrderHistoryVariant ? filterContractStatus : '',
    timeFilterType,
    dateFilter: filterDateFilter,
    from: filterFrom,
    to: filterTo,
    createdDateFilter: filterCreatedDateFilter,
    createdFrom: filterCreatedFrom,
    createdTo: filterCreatedTo,
    ...overrides,
  });

  const handleApplyFilter = () => {
    const newFilter = buildFilterPayload();
    if (onApply) onApply(newFilter);
    if (closeDropdown) closeDropdown();
  };

  const handleClearFilter = () => {
    const [fromDate, toDate] = getDateFilterOption(dateFilter.tatCaThoiGian);
    const newFilter = {
      page: 0,
      limit: AppConfigs.defaultPageLimit,
      q: '',
      primaryRole: '',
      buyerType: '',
      status: '',
      createdBy: '',
      invoiceStatus: '',
      orderType: defaultOrderType ?? '',
      contractStatus: '',
      timeFilterType: 'CREATED_DATE',
      dateFilter: dateFilter.tatCaThoiGian,
      from: fromDate.format('YYYY-MM-DD'),
      to: toDate.format('YYYY-MM-DD'),
      createdDateFilter: dateFilter.tatCaThoiGian,
      createdFrom: fromDate.format('YYYY-MM-DD'),
      createdTo: toDate.format('YYYY-MM-DD'),
    };
    if (onCancel) onCancel(newFilter);
    if (closeDropdown) closeDropdown();
  };

  const handleDateSave = (selectedValue, startDate, endDate) => {
    if (timeFilterType === 'CREATED_DATE') {
      setFilterCreatedDateFilter(selectedValue);
      setFilterCreatedFrom(startDate);
      setFilterCreatedTo(endDate);
    } else {
      setFilterDateFilter(selectedValue);
      setFilterFrom(startDate);
      setFilterTo(endDate);
    }
  };

  const activeDateFilter =
    timeFilterType === 'CREATED_DATE' ? filterCreatedDateFilter : filterDateFilter;
  const activeDateFrom =
    timeFilterType === 'CREATED_DATE' ? filterCreatedFrom : filterFrom;
  const activeDateTo =
    timeFilterType === 'CREATED_DATE' ? filterCreatedTo : filterTo;

  return (
    <div className="py-4">
      <div className="row m-0 px-2">
        <div className="col-12 mb-3 px-2">
          <KTFormSearch
            name="search_filter"
            placeholder="Theo mã đơn hàng, tên nhà đầu tư..."
            value={filterQ}
            onSubmit={(text) => {
              setFilterQ(text);
            }}
          />
        </div>

        <div className="col-12 px-2">
          <div className="row m-0">
            {isOrderHistoryVariant ? (
              <>
                <KTDropdownFilter
                  label="Loại đơn hàng"
                  filterKey="orderType"
                  options={[
                    { name: 'Tất cả', value: '' },
                    ...AgencyHelper.Data.orderTypeOptions,
                  ]}
                  selectedValue={filterOrderType}
                  isInDropdown={true}
                  additionalClassName="pr-2 pb-3"
                  handleChange={setFilterOrderType}
                />
                <KTDropdownFilter
                  label="Trạng thái đơn hàng"
                  filterKey="status"
                  options={[
                    { name: 'Tất cả', value: '' },
                    ...AgencyHelper.Data.orderStatusOptions,
                  ]}
                  selectedValue={filterStatus}
                  isInDropdown={true}
                  additionalClassName="pl-2 pb-3"
                  handleChange={setFilterStatus}
                />
                <KTDropdownFilter
                  label="Trạng thái hợp đồng"
                  filterKey="contractStatus"
                  options={[
                    { name: 'Tất cả', value: '' },
                    ...AgencyHelper.Data.contractOptions,
                  ]}
                  selectedValue={filterContractStatus}
                  isInDropdown={true}
                  additionalClassName="pr-2 pb-3"
                  handleChange={setFilterContractStatus}
                />
                <KTDropdownFilter
                  label="Người tạo đơn"
                  filterKey="createdBy"
                  options={DealerHelper.filterCreatedByOptions()}
                  selectedValue={filterCreatedBy}
                  isInDropdown={true}
                  additionalClassName="pl-2 pb-3"
                  handleChange={setFilterCreatedBy}
                />
                <KTDropdownFilter
                  label="Trạng thái xuất hóa đơn"
                  filterKey="invoiceStatus"
                  options={invoiceStatusOptions}
                  selectedValue={filterInvoiceStatus}
                  isInDropdown={true}
                  additionalClassName="pr-2 pb-3"
                  handleChange={setFilterInvoiceStatus}
                />
              </>
            ) : (
              <>
                <KTDropdownFilter
                  label="Vai trò"
                  filterKey="primaryRole"
                  options={buyerRoleOptions}
                  selectedValue={filterPrimaryRole}
                  isInDropdown={true}
                  additionalClassName="pr-2 pb-3"
                  handleChange={setFilterPrimaryRole}
                />
                <KTDropdownFilter
                  label="Loại nhà đầu tư"
                  filterKey="buyerType"
                  options={DealerHelper.filterAgencyTypeOptions()}
                  selectedValue={filterBuyerType}
                  isInDropdown={true}
                  additionalClassName="pl-2 pb-3"
                  handleChange={setFilterBuyerType}
                />
                <KTDropdownFilter
                  label="Trạng thái"
                  filterKey="status"
                  options={[
                    { name: 'Tất cả', value: '' },
                    ...AgencyHelper.Data.orderStatusOptions,
                  ]}
                  selectedValue={filterStatus}
                  isInDropdown={true}
                  additionalClassName="pr-2 pb-3"
                  handleChange={setFilterStatus}
                />
                <KTDropdownFilter
                  label="Người tạo"
                  filterKey="createdBy"
                  options={DealerHelper.filterCreatedByOptions()}
                  selectedValue={filterCreatedBy}
                  isInDropdown={true}
                  additionalClassName="pl-2 pb-3"
                  handleChange={setFilterCreatedBy}
                />
              </>
            )}
          </div>
        </div>

        <div
          className="col-12 px-2 pt-3 mt-1"
          style={{ borderTop: `1px solid ${AppColors.gray[200]}` }}
        >
          <div className="radio-inline w-100 mb-3">
            <div className="d-flex gap-5">
              <KTRadio
                name="createdDate"
                label="Ngày tạo đơn"
                type={KTRadioType.outline}
                additionalClassName="radio-success"
                onChange={() => {
                  setTimeFilterType('CREATED_DATE');
                }}
                checked={timeFilterType === 'CREATED_DATE'}
              />
              <KTRadio
                name="completedDate"
                label="Ngày hoàn thành"
                type={KTRadioType.outline}
                additionalClassName="radio-success"
                onChange={() => {
                  setTimeFilterType('COMPLETED_DATE');
                }}
                checked={timeFilterType === 'COMPLETED_DATE'}
              />
            </div>
          </div>
          <div className="mb-2">
            {UIHelper.buildFormLabel(
              timeFilterType === 'CREATED_DATE' ? 'Ngày tạo đơn' : 'Ngày hoàn thành'
            )}
            <CustomDateRangePicker
              key={timeFilterType}
              inlineMode={true}
              toggleElementBorderRadius={5}
              initialValue={activeDateFilter || dateFilter.tatCaThoiGian}
              filterComponent={{
                from: activeDateFrom,
                to: activeDateTo,
              }}
              onPressSave={handleDateSave}
            />
          </div>
        </div>
      </div>

      <div
        className="px-4 pt-4 pb-0 d-flex align-items-center justify-content-end gap-3"
        style={{
          borderTop: `1px solid ${AppColors.gray[200]}`,
        }}
      >
        {UIHelper.buildNegativeButton('Đặt lại mặc định', handleClearFilter)}
        {UIHelper.buildGreenButton('Lọc ngay', handleApplyFilter)}
      </div>
    </div>
  );
}

export default AgencySaleOrderListFilterView;
